/**
 * AutoHelpLayer — global hover layer.
 *
 * When Help Mode is ON, hovering ANY interactive element on the page (button,
 * link, [role=button], [data-help]) shows a floating help bubble with that
 * element's help text. Source of help text, in priority order:
 *
 *   1. data-help-title + data-help-description  (richest — author wrote it)
 *   2. data-help                                (single-string description)
 *   3. title attribute
 *   4. aria-label attribute
 *   5. button textContent (last resort, ≤80 chars)
 *
 * This lets every Button on the site participate in help mode without anyone
 * having to wrap each Button in a <HelpBubble>. The existing <HelpBubble>
 * component still works and takes precedence for richer / nested-link cases
 * (Learn more, Ask BB AI buttons).
 *
 * No per-element dot indicator — the Help button in the header is the single
 * "help mode is ON" signal. See feedback in CLAUDE.md.
 */

"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import Link from "next/link";
import { useHelp } from "@/contexts/help-context";
import { BookOpen, MessageCircle } from "lucide-react";
import { cn } from "@/lib/utils";

interface BubbleState {
  title: string;
  description: string;
  learnMoreAnchor?: string;
  aiQuestion?: string;
  top: number;
  left: number;
  side: "top" | "bottom";
}

const TRACKED_SELECTOR =
  "button, a[href], [role='button'], [data-help], [data-help-description], [data-help-title]";

/** What a person can SEE in the element — never the CSS of an inline <style>
 *  or a hidden script (Gordon 2026-11-01: hovering a release showed
 *  "@keyframes bb-slide-flash …"). innerText skips display:none content. */
function visibleText(el: HTMLElement): string {
  return (el.innerText ?? '').replace(/\s+/g, ' ').trim();
}

/** Pull help metadata off an element. Returns null if nothing usable found. */
function readHelp(el: HTMLElement): { title: string; description: string; learnMoreAnchor?: string; aiQuestion?: string } | null {
  // Skip if explicitly opted out
  if (el.dataset.helpSkip === "true") return null;
  // Skip if wrapped by an explicit <HelpBubble> — that one wins
  if (el.closest("[data-help-bubble-wrapped='true']")) return null;

  const t = el.dataset.helpTitle?.trim();
  const d = el.dataset.helpDescription?.trim();
  if (t && d) {
    return {
      title: t,
      description: d,
      learnMoreAnchor: el.dataset.helpAnchor?.trim() || undefined,
      aiQuestion: el.dataset.helpAi?.trim() || undefined,
    };
  }

  // data-help on its own = description-only
  const dataHelp = el.dataset.help?.trim();
  if (dataHelp) {
    const inferredTitle = el.dataset.helpTitle?.trim()
      || el.getAttribute("aria-label")?.trim()
      || visibleText(el).slice(0, 40)
      || "Help";
    return {
      title: inferredTitle,
      description: dataHelp,
      learnMoreAnchor: el.dataset.helpAnchor?.trim() || undefined,
      aiQuestion: el.dataset.helpAi?.trim() || undefined,
    };
  }

  const titleAttr = el.getAttribute("title")?.trim();
  const ariaLabel = el.getAttribute("aria-label")?.trim();
  const text      = visibleText(el).slice(0, 80);

  // Need at least a description-ish thing — title / aria-label / text
  const description = titleAttr || ariaLabel || text;
  if (!description) return null;

  // Title — prefer aria-label or button text, fall back to "Action"
  const titleStr = ariaLabel || text || titleAttr || "Action";
  // Same string for both? Just show description.
  const finalTitle = titleStr === description ? "" : titleStr;
  return { title: finalTitle || "Action", description };
}

export function AutoHelpLayer() {
  const { helpEnabled, openChatWithAI } = useHelp();
  const [bubble, setBubble] = useState<BubbleState | null>(null);
  const lastTargetRef = useRef<HTMLElement | null>(null);
  const hideTimerRef = useRef<number | null>(null);

  const computePosition = useCallback((el: HTMLElement): { top: number; left: number; side: "top" | "bottom" } => {
    const r = el.getBoundingClientRect();
    // Prefer top; fall back to bottom if no room.
    const wantsTop = r.top > 80;
    const top = wantsTop ? r.top - 8 : r.bottom + 8;
    const left = Math.max(8, Math.min(window.innerWidth - 280, r.left + r.width / 2 - 130));
    return { top, left, side: wantsTop ? "top" : "bottom" };
  }, []);

  useEffect(() => {
    if (!helpEnabled) {
      setBubble(null);
      return;
    }

    const showFor = (el: HTMLElement) => {
      const help = readHelp(el);
      if (!help) {
        setBubble(null);
        return;
      }
      const pos = computePosition(el);
      lastTargetRef.current = el;
      setBubble({ ...help, ...pos });
    };

    const onOver = (e: MouseEvent) => {
      const target = (e.target as HTMLElement | null)?.closest(TRACKED_SELECTOR) as HTMLElement | null;
      if (!target) return;
      if (target === lastTargetRef.current) return;
      if (hideTimerRef.current) { window.clearTimeout(hideTimerRef.current); hideTimerRef.current = null; }
      showFor(target);
    };

    const onOut = (e: MouseEvent) => {
      const target = (e.target as HTMLElement | null)?.closest(TRACKED_SELECTOR);
      if (!target) return;
      // Tiny debounce so leaving an element + entering an adjacent one doesn't flicker
      if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = window.setTimeout(() => {
        lastTargetRef.current = null;
        setBubble(null);
      }, 120);
    };

    const onScroll = () => {
      // Reposition or hide on scroll
      if (lastTargetRef.current) {
        const pos = computePosition(lastTargetRef.current);
        setBubble((b) => (b ? { ...b, ...pos } : b));
      }
    };

    document.addEventListener("mouseover", onOver, true);
    document.addEventListener("mouseout",  onOut, true);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);

    return () => {
      document.removeEventListener("mouseover", onOver, true);
      document.removeEventListener("mouseout",  onOut, true);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
      if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
    };
  }, [helpEnabled, computePosition]);

  if (!helpEnabled || !bubble) return null;

  return (
    <div
      role="tooltip"
      aria-live="polite"
      className={cn(
        "fixed z-[200] pointer-events-auto",
        // The selected theme's own pop-up colours, corner radius and accent border
        // (Gordon 2026-11-01); custom CSS themes restyle .bb-help-bubble.
        "bb-help-bubble max-w-[280px] overflow-hidden rounded-[var(--radius,0.5rem)] border-2 border-primary bg-popover text-popover-foreground shadow-xl",
        "transition-opacity duration-100"
      )}
      style={{
        top: bubble.side === "top" ? bubble.top : bubble.top,
        left: bubble.left,
        transform: bubble.side === "top" ? "translateY(-100%)" : undefined,
      }}
      // Keep the bubble open while hovered (so users can click Learn more / Ask AI)
      onMouseEnter={() => { if (hideTimerRef.current) { window.clearTimeout(hideTimerRef.current); hideTimerRef.current = null; } }}
      onMouseLeave={() => { lastTargetRef.current = null; setBubble(null); }}
    >
      <div className="px-3 pt-3 pb-2">
        {bubble.title && <p className="font-semibold text-sm leading-tight mb-1">{bubble.title}</p>}
        <p className="text-xs text-muted-foreground leading-relaxed">{bubble.description}</p>
      </div>
      {(bubble.learnMoreAnchor || bubble.aiQuestion) && (
        <div className="flex items-center gap-3 px-3 py-2 border-t bg-muted/30">
          {bubble.learnMoreAnchor && (
            <Link
              href={`/help#${bubble.learnMoreAnchor}`}
              className="flex items-center gap-1 text-[11px] text-primary hover:underline"
            >
              <BookOpen className="w-3 h-3 flex-shrink-0" />
              Learn more
            </Link>
          )}
          {bubble.aiQuestion && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); openChatWithAI(bubble.aiQuestion!); }}
              className="flex items-center gap-1 text-[11px] text-primary hover:underline ml-auto"
            >
              <MessageCircle className="w-3 h-3 flex-shrink-0" />
              Ask BB AI
            </button>
          )}
        </div>
      )}
    </div>
  );
}
